if (!window.Eurus.loadedScript.has('product-miura.js')) {
  window.Eurus.loadedScript.add('product-miura.js');

  document.addEventListener('alpine:init', () => {
    Alpine.data('xProductMiura', () => ({
      activeTab: 'shaft',
      tabOrder: ['shaft', 'grip', 'loft'],
      fields: {},
      isValid: false,

      init() {
        this.root = this.$el;
        this.productTemplate = this.root.closest('[id^="x-product-template-"]');
        this.tabs = Array.from(this.root.querySelectorAll('[data-miura-tab]'));
        this.panels = Array.from(this.root.querySelectorAll('[data-miura-panel]'));
        this.nextButtons = Array.from(this.root.querySelectorAll('[data-miura-next]'));
        this.optionButtons = Array.from(this.root.querySelectorAll('button[data-miura-field][data-miura-value]'));
        this.propertyInputs = Array.from(this.root.querySelectorAll('[data-miura-property-input]'));
        this.addToCartObservers = [];
        this.isSyncingAddToCart = false;
        this.availableShaftBrands = [];
        this.availableShaftModels = [];
        this.availableGripBrands = [];
        this.availableGripModels = [];
        this.availableFerruleModels = [];
        this.hasValidShaftSelection = false;
        this.hasValidGripSelection = false;
        this.hasValidFerruleSelection = false;

        const initialTab = this.tabs.find((tab) => {
          return tab.classList.contains('active') || tab.getAttribute('aria-selected') === 'true';
        });

        if (initialTab) {
          this.activeTab = initialTab.dataset.miuraTab;
        }

        this.captureInitialState();
        this.loadShaftData();
        this.loadGripData();
        this.loadFerruleData();
        this.bindAddToCartButtons();
        this.bindTabHandlers();
        this.bindNavigationHandlers();
        this.bindFieldHandlers();
        this.initializeShaftState();
        this.initializeGripState();
        this.initializeFerruleState();
        this.updateUI();
        this.syncConfiguratorState();
      },

      destroy() {
        this.addToCartObservers.forEach((observer) => observer.disconnect());
      },

      captureInitialState() {
        this.optionButtons.forEach((button) => {
          if (button.classList.contains('selected')) {
            this.fields[button.dataset.miuraField] = button.dataset.miuraValue;
          }
        });

        this.root.querySelectorAll('select[data-miura-field], textarea[data-miura-field]').forEach((input) => {
          this.fields[input.dataset.miuraField] = input.value;
        });

        this.root.querySelectorAll('input[type="checkbox"][data-miura-field]').forEach((input) => {
          this.fields[input.dataset.miuraField] = input.checked;
        });
      },

      loadShaftData() {
        const fallbackData = {
          defaults: {
            material: 'steel',
            flex: 'stiff',
            brand: 'True Temper',
          },
          brands: [],
        };
        const shaftDataScript = this.root.querySelector('[data-miura-shaft-data]');
        const parsedData = this.parseJsonScript(shaftDataScript, fallbackData);

        this.shaftDefaults = {
          material: this.normalizeToken(parsedData.defaults?.material || fallbackData.defaults.material),
          flex: this.normalizeToken(parsedData.defaults?.flex || fallbackData.defaults.flex),
          brand: this.normalizeLabel(parsedData.defaults?.brand || fallbackData.defaults.brand),
        };
        this.shaftBrands = Array.isArray(parsedData.brands)
          ? parsedData.brands.map((brand) => {
              return {
                brandLabel: this.normalizeLabel(brand.brandLabel),
                productId: brand.productId,
                productHandle: brand.productHandle,
                materials: Array.isArray(brand.materials)
                  ? brand.materials.map((material) => this.normalizeToken(material)).filter(Boolean)
                  : [],
                models: Array.isArray(brand.models)
                  ? brand.models.map((model) => {
                      return {
                        variantId: String(model.variantId || ''),
                        title: this.normalizeLabel(model.title),
                        label: this.normalizeLabel(model.label),
                        summaryLabel: this.normalizeLabel(model.summaryLabel),
                        material: this.normalizeToken(model.material),
                        flex: this.normalizeToken(model.flex),
                        available: Boolean(model.available),
                        imageSrc: model.imageSrc || '',
                        imageAlt: this.normalizeLabel(model.imageAlt),
                      };
                    })
                  : [],
              };
            })
          : [];
        this.shaftBrandSelect = this.root.querySelector('select[data-miura-field="shaft_brand"]');
        this.shaftModelSelect = this.root.querySelector('select[data-miura-field="shaft_model"]');
        this.shaftPreviewImage = this.root.querySelector('[data-miura-shaft-preview-image]');
        this.shaftPreviewEmpty = this.root.querySelector('[data-miura-shaft-preview-empty]');
        this.sstPuringSelect = this.root.querySelector('[data-miura-sst-select]');
        this.shaftLinkedInputs = Array.from(this.root.querySelectorAll('[data-miura-shaft-linked-input]'));
        this.sstLinkedInputs = Array.from(this.root.querySelectorAll('[data-miura-sst-linked-input]'));
      },

      loadGripData() {
        const fallbackData = {
          defaults: {
            brand: 'Miura',
          },
          brands: [],
        };
        const gripDataScript = this.root.querySelector('[data-miura-grip-data]');
        const parsedData = this.parseJsonScript(gripDataScript, fallbackData);

        this.gripDefaults = {
          brand: this.normalizeLabel(parsedData.defaults?.brand || fallbackData.defaults.brand),
        };
        this.gripBrands = Array.isArray(parsedData.brands)
          ? parsedData.brands.map((brand) => {
              return {
                brandLabel: this.normalizeLabel(brand.brandLabel),
                productId: brand.productId,
                productHandle: brand.productHandle,
                models: Array.isArray(brand.models)
                  ? brand.models.map((model) => {
                      return {
                        variantId: String(model.variantId || ''),
                        title: this.normalizeLabel(model.title),
                        label: this.normalizeLabel(model.label),
                        summaryLabel: this.normalizeLabel(model.summaryLabel),
                        available: Boolean(model.available),
                        imageSrc: model.imageSrc || '',
                        imageAlt: this.normalizeLabel(model.imageAlt),
                      };
                    })
                  : [],
              };
            })
          : [];
        this.gripBrandSelect = this.root.querySelector('select[data-miura-field="grip_brand"]');
        this.gripModelSelect = this.root.querySelector('select[data-miura-field="grip_model"]');
        this.gripPreviewImage = this.root.querySelector('[data-miura-grip-preview-image]');
        this.gripPreviewEmpty = this.root.querySelector('[data-miura-grip-preview-empty]');
        this.gripLinkedInputs = Array.from(this.root.querySelectorAll('[data-miura-grip-linked-input]'));
      },

      loadFerruleData() {
        const fallbackData = {
          defaults: {
            title: 'Standard Black',
          },
          models: [],
        };
        const ferruleDataScript = this.root.querySelector('[data-miura-ferrule-data]');
        const parsedData = this.parseJsonScript(ferruleDataScript, fallbackData);

        this.ferruleDefaults = {
          title: this.normalizeLabel(parsedData.defaults?.title || fallbackData.defaults.title),
        };
        this.ferruleModels = Array.isArray(parsedData.models)
          ? parsedData.models.map((model) => {
              return {
                variantId: String(model.variantId || ''),
                title: this.normalizeLabel(model.title),
                label: this.normalizeLabel(model.label),
                summaryLabel: this.normalizeLabel(model.summaryLabel),
                available: Boolean(model.available),
                imageSrc: model.imageSrc || '',
                imageAlt: this.normalizeLabel(model.imageAlt),
              };
            })
          : [];
        this.ferruleSelect = this.root.querySelector('select[data-miura-field="ferrule"]');
        this.ferrulePreviewImage = this.root.querySelector('[data-miura-ferrule-preview-image]');
        this.ferrulePreviewEmpty = this.root.querySelector('[data-miura-ferrule-preview-empty]');
        this.ferruleLinkedInputs = Array.from(this.root.querySelectorAll('[data-miura-ferrule-linked-input]'));
      },

      parseJsonScript(script, fallback) {
        if (!script || !script.textContent.trim()) {
          return fallback;
        }

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error('Failed to parse Miura configurator data.', error);
          return fallback;
        }
      },

      bindAddToCartButtons() {
        if (!this.productTemplate) {
          this.addToCartButtons = [];
          return;
        }

        this.addToCartButtons = Array.from(this.productTemplate.querySelectorAll('.add_to_cart_button'));

        this.addToCartButtons.forEach((button) => {
          button.dataset.miuraNativeDisabled = button.disabled ? 'true' : 'false';

          const observer = new MutationObserver(() => {
            if (this.isSyncingAddToCart) {
              return;
            }

            button.dataset.miuraNativeDisabled = button.disabled ? 'true' : 'false';
            this.applyAddToCartState();
          });

          observer.observe(button, {
            attributes: true,
            attributeFilter: ['disabled'],
          });

          this.addToCartObservers.push(observer);
        });
      },

      bindTabHandlers() {
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', (event) => {
            event.preventDefault();

            if (tab.disabled) {
              return;
            }

            this.setActiveTab(tab.dataset.miuraTab);
          });
        });
      },

      bindNavigationHandlers() {
        this.nextButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            const panelName = button.closest('[data-miura-panel]')?.dataset.miuraPanel || this.activeTab;

            if (!this.canAdvanceFromTab(panelName)) {
              return;
            }

            this.goToRelativeTab(1);
          });
        });

        this.root.querySelectorAll('[data-miura-prev]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.goToRelativeTab(-1);
          });
        });
      },

      bindFieldHandlers() {
        this.optionButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.setFieldValue(button.dataset.miuraField, button.dataset.miuraValue);
          });
        });

        this.root.querySelectorAll('select[data-miura-field]').forEach((input) => {
          input.addEventListener('change', () => {
            const fieldName = input.dataset.miuraField;

            if (fieldName === 'shaft_brand') {
              this.fields.shaft_brand = this.normalizeLabel(input.value);
              this.refreshShaftOptions({ preserveBrand: true, preserveModel: false });
              this.syncConfiguratorState();
              return;
            }

            if (fieldName === 'shaft_model') {
              this.fields.shaft_model = input.value;
              this.updateShaftPreview();
              this.syncConfiguratorState();
              return;
            }

            if (fieldName === 'grip_brand') {
              this.fields.grip_brand = this.normalizeLabel(input.value);
              this.refreshGripOptions({ preserveBrand: true, preserveModel: false });
              this.syncConfiguratorState();
              return;
            }

            if (fieldName === 'grip_model') {
              this.fields.grip_model = input.value;
              this.updateGripPreview();
              this.syncConfiguratorState();
              return;
            }

            if (fieldName === 'ferrule') {
              this.fields.ferrule = input.value;
              this.updateFerrulePreview();
              this.syncConfiguratorState();
              return;
            }

            this.fields[fieldName] = input.value;
            this.syncConfiguratorState();
          });
        });

        this.root.querySelectorAll('textarea[data-miura-field]').forEach((input) => {
          input.addEventListener('input', () => {
            this.fields[input.dataset.miuraField] = input.value;
            this.syncConfiguratorState();
          });
        });

        this.root.querySelectorAll('input[type="checkbox"][data-miura-field]').forEach((input) => {
          input.addEventListener('change', () => {
            this.fields[input.dataset.miuraField] = input.checked;
            this.syncConfiguratorState();
          });
        });
      },

      initializeShaftState() {
        this.fields.material = this.shaftDefaults.material;
        this.fields.flex = this.shaftDefaults.flex;
        this.fields.shaft_brand = this.shaftDefaults.brand;
        this.fields.shaft_model = '';
        this.refreshShaftOptions({ preserveBrand: false, preserveModel: false });
      },

      initializeGripState() {
        this.fields.grip_brand = this.gripDefaults.brand;
        this.fields.grip_model = '';
        this.refreshGripOptions({ preserveBrand: false, preserveModel: false });
      },

      initializeFerruleState() {
        this.fields.ferrule = '';
        this.refreshFerruleOptions({ preserveModel: false });
      },

      normalizeToken(value) {
        return `${value ?? ''}`.trim().toLowerCase();
      },

      normalizeLabel(value) {
        return `${value ?? ''}`.trim();
      },

      labelsMatch(left, right) {
        return this.normalizeToken(left) === this.normalizeToken(right);
      },

      setActiveTab(tabName) {
        if (!this.tabOrder.includes(tabName)) {
          return;
        }

        if (tabName !== this.activeTab && !this.isTabAccessible(tabName)) {
          return;
        }

        this.activeTab = tabName;
        this.updateUI();
      },

      goToRelativeTab(step) {
        const currentIndex = this.tabOrder.indexOf(this.activeTab);

        if (currentIndex === -1) {
          this.activeTab = this.tabOrder[0];
          this.updateUI();
          return;
        }

        if (step > 0 && !this.canAdvanceFromTab(this.activeTab)) {
          this.updateUI();
          return;
        }

        const nextIndex = Math.min(this.tabOrder.length - 1, Math.max(0, currentIndex + step));
        this.setActiveTab(this.tabOrder[nextIndex]);
      },

      isTabAccessible(tabName) {
        if (tabName === 'shaft') {
          return true;
        }

        if (tabName === 'grip') {
          return this.hasValidShaftSelection;
        }

        if (tabName === 'loft') {
          return this.hasValidShaftSelection && this.hasValidGripSelection;
        }

        return false;
      },

      canAdvanceFromTab(tabName) {
        if (tabName === 'shaft') {
          return this.hasValidShaftSelection;
        }

        if (tabName === 'grip') {
          return this.hasValidGripSelection;
        }

        return true;
      },

      getFirstAccessibleTab() {
        return this.tabOrder.find((tabName) => this.isTabAccessible(tabName)) || this.tabOrder[0];
      },

      setFieldValue(fieldName, fieldValue) {
        this.fields[fieldName] = fieldName === 'material' || fieldName === 'flex'
          ? this.normalizeToken(fieldValue)
          : fieldValue;
        this.updateOptionButtonGroup(fieldName);

        if (fieldName === 'material' || fieldName === 'flex') {
          this.refreshShaftOptions({ preserveBrand: true, preserveModel: false });
        }

        this.syncConfiguratorState();
      },

      syncConfiguratorState() {
        this.syncShaftLinkedProduct();
        this.syncGripLinkedProduct();
        this.syncSstPuringLinkedProduct();
        this.syncFerruleLinkedProduct();
        this.syncMainProductProperties();
        this.isValid = this.getIsConfiguratorValid();
        this.root.dataset.miuraValid = this.isValid ? 'true' : 'false';
        this.syncInteractiveState();
      },

      syncInteractiveState() {
        this.applyAddToCartState();

        if (!this.isTabAccessible(this.activeTab)) {
          this.updateUI();
          return;
        }

        this.updateNavigationState();
      },

      getIsConfiguratorValid() {
        const buttonGroups = this.getOptionButtonGroups();
        const buttonGroupsValid = Object.keys(buttonGroups).every((fieldName) => this.hasValue(this.fields[fieldName]));
        const selectsValid = Array.from(this.root.querySelectorAll('select[data-miura-field]:not([disabled])')).every((input) => {
          return this.hasValue(this.fields[input.dataset.miuraField]);
        });
        const requiredTextareasValid = Array.from(this.root.querySelectorAll('textarea[data-miura-field][required]')).every((input) => {
          return this.hasValue(this.fields[input.dataset.miuraField]);
        });
        const requiredCheckboxesValid = Array.from(this.root.querySelectorAll('input[type="checkbox"][data-miura-field][required]')).every((input) => {
          return this.fields[input.dataset.miuraField] === true;
        });

        return this.hasValidShaftSelection
          && this.hasValidGripSelection
          && this.hasValidFerruleSelection
          && buttonGroupsValid
          && selectsValid
          && requiredTextareasValid
          && requiredCheckboxesValid;
      },

      hasValue(value) {
        return value !== undefined && value !== null && value !== '';
      },

      getAvailableShaftBrands(material, flex) {
        return this.shaftBrands.filter((brand) => {
          return brand.materials.includes(material)
            && brand.models.some((model) => model.material === material && model.flex === flex);
        });
      },

      getAvailableShaftModels(brandLabel, material, flex) {
        const shaftBrand = this.shaftBrands.find((brand) => {
          return this.labelsMatch(brand.brandLabel, brandLabel);
        });

        if (!shaftBrand) {
          return [];
        }

        return shaftBrand.models.filter((model) => {
          return model.material === material && model.flex === flex;
        });
      },

      getSelectedShaftModel() {
        return this.availableShaftModels.find((model) => {
          return model.variantId === String(this.fields.shaft_model || '');
        }) || null;
      },

      getAvailableGripBrands() {
        return this.gripBrands;
      },

      getAvailableGripModels(brandLabel) {
        const gripBrand = this.gripBrands.find((brand) => {
          return this.labelsMatch(brand.brandLabel, brandLabel);
        });

        if (!gripBrand) {
          return [];
        }

        return gripBrand.models;
      },

      getSelectedGripModel() {
        return this.availableGripModels.find((model) => {
          return model.variantId === String(this.fields.grip_model || '');
        }) || null;
      },

      getAvailableFerruleModels() {
        return this.ferruleModels.filter((model) => {
          return model.available;
        });
      },

      getSelectedFerruleModel() {
        return this.availableFerruleModels.find((model) => {
          return model.variantId === String(this.fields.ferrule || '');
        }) || null;
      },

      refreshShaftOptions({ preserveBrand = true, preserveModel = false } = {}) {
        const material = this.normalizeToken(this.fields.material || this.shaftDefaults.material);
        const flex = this.normalizeToken(this.fields.flex || this.shaftDefaults.flex);
        const requestedBrand = preserveBrand ? this.fields.shaft_brand : this.shaftDefaults.brand;
        const requestedModel = preserveModel ? this.fields.shaft_model : '';

        this.availableShaftBrands = this.getAvailableShaftBrands(material, flex);

        const nextBrand = this.resolveBrandSelection(requestedBrand);
        this.fields.shaft_brand = this.renderBrandOptions(nextBrand);

        this.availableShaftModels = this.fields.shaft_brand
          ? this.getAvailableShaftModels(this.fields.shaft_brand, material, flex)
          : [];

        const nextModel = this.resolveModelSelection(requestedModel);
        this.fields.shaft_model = this.renderModelOptions(nextModel);
        this.hasValidShaftSelection = this.availableShaftBrands.length > 0
          && this.availableShaftModels.length > 0
          && this.hasValue(this.fields.shaft_brand)
          && this.hasValue(this.fields.shaft_model);
        this.updateShaftPreview();
      },

      refreshGripOptions({ preserveBrand = true, preserveModel = false } = {}) {
        const requestedBrand = preserveBrand ? this.fields.grip_brand : this.gripDefaults.brand;
        const requestedModel = preserveModel ? this.fields.grip_model : '';

        this.availableGripBrands = this.getAvailableGripBrands();

        const nextBrand = this.resolveGripBrandSelection(requestedBrand);
        this.fields.grip_brand = this.renderGripBrandOptions(nextBrand);

        this.availableGripModels = this.fields.grip_brand
          ? this.getAvailableGripModels(this.fields.grip_brand)
          : [];

        const nextModel = this.resolveGripModelSelection(requestedModel);
        this.fields.grip_model = this.renderGripModelOptions(nextModel);
        this.hasValidGripSelection = this.availableGripBrands.length > 0
          && this.availableGripModels.length > 0
          && this.hasValue(this.fields.grip_brand)
          && this.hasValue(this.fields.grip_model);
        this.updateGripPreview();
      },

      refreshFerruleOptions({ preserveModel = false } = {}) {
        const requestedModel = preserveModel ? this.fields.ferrule : '';

        this.availableFerruleModels = this.getAvailableFerruleModels();

        const nextModel = this.resolveFerruleModelSelection(requestedModel);
        this.fields.ferrule = this.renderFerruleOptions(nextModel);
        this.hasValidFerruleSelection = this.hasValue(this.fields.ferrule);
        this.updateFerrulePreview();
      },

      resolveBrandSelection(requestedBrand) {
        const matchingRequestedBrand = this.availableShaftBrands.find((brand) => {
          return this.labelsMatch(brand.brandLabel, requestedBrand);
        });

        if (matchingRequestedBrand) {
          return matchingRequestedBrand.brandLabel;
        }

        const defaultBrand = this.availableShaftBrands.find((brand) => {
          return this.labelsMatch(brand.brandLabel, this.shaftDefaults.brand);
        });

        if (defaultBrand) {
          return defaultBrand.brandLabel;
        }

        return this.availableShaftBrands[0] ? this.availableShaftBrands[0].brandLabel : '';
      },

      resolveModelSelection(requestedModel) {
        const matchingRequestedModel = this.availableShaftModels.find((model) => {
          return model.variantId === String(requestedModel);
        });

        if (matchingRequestedModel) {
          return matchingRequestedModel.variantId;
        }

        return this.availableShaftModels[0] ? this.availableShaftModels[0].variantId : '';
      },

      resolveGripBrandSelection(requestedBrand) {
        const matchingRequestedBrand = this.availableGripBrands.find((brand) => {
          return this.labelsMatch(brand.brandLabel, requestedBrand);
        });

        if (matchingRequestedBrand) {
          return matchingRequestedBrand.brandLabel;
        }

        const defaultBrand = this.availableGripBrands.find((brand) => {
          return this.labelsMatch(brand.brandLabel, this.gripDefaults.brand);
        });

        if (defaultBrand) {
          return defaultBrand.brandLabel;
        }

        return this.availableGripBrands[0] ? this.availableGripBrands[0].brandLabel : '';
      },

      resolveGripModelSelection(requestedModel) {
        const matchingRequestedModel = this.availableGripModels.find((model) => {
          return model.variantId === String(requestedModel);
        });

        if (matchingRequestedModel) {
          return matchingRequestedModel.variantId;
        }

        return this.availableGripModels[0] ? this.availableGripModels[0].variantId : '';
      },

      resolveFerruleModelSelection(requestedModel) {
        const matchingRequestedModel = this.availableFerruleModels.find((model) => {
          return model.variantId === String(requestedModel);
        });

        if (matchingRequestedModel) {
          return matchingRequestedModel.variantId;
        }

        const defaultModel = this.availableFerruleModels.find((model) => {
          return this.labelsMatch(model.title, this.ferruleDefaults.title);
        });

        if (defaultModel) {
          return defaultModel.variantId;
        }

        return this.availableFerruleModels[0] ? this.availableFerruleModels[0].variantId : '';
      },

      renderBrandOptions(selectedBrand) {
        if (!this.shaftBrandSelect) {
          return '';
        }

        const brandOptions = this.availableShaftBrands.map((brand) => {
          return {
            value: brand.brandLabel,
            label: brand.brandLabel,
          };
        });

        return this.replaceSelectOptions(this.shaftBrandSelect, brandOptions, selectedBrand, 'No shaft brands available');
      },

      renderModelOptions(selectedModel) {
        if (!this.shaftModelSelect) {
          return '';
        }

        const modelOptions = this.availableShaftModels.map((model) => {
          return {
            value: model.variantId,
            label: model.label,
          };
        });

        return this.replaceSelectOptions(this.shaftModelSelect, modelOptions, selectedModel, 'No shaft models available');
      },

      renderGripBrandOptions(selectedBrand) {
        if (!this.gripBrandSelect) {
          return '';
        }

        const brandOptions = this.availableGripBrands.map((brand) => {
          return {
            value: brand.brandLabel,
            label: brand.brandLabel,
          };
        });

        return this.replaceSelectOptions(this.gripBrandSelect, brandOptions, selectedBrand, 'No grip brands available');
      },

      renderGripModelOptions(selectedModel) {
        if (!this.gripModelSelect) {
          return '';
        }

        const modelOptions = this.availableGripModels.map((model) => {
          return {
            value: model.variantId,
            label: model.label,
          };
        });

        return this.replaceSelectOptions(this.gripModelSelect, modelOptions, selectedModel, 'No grip models available');
      },

      renderFerruleOptions(selectedModel) {
        if (!this.ferruleSelect) {
          return '';
        }

        const ferruleOptions = this.availableFerruleModels.map((model) => {
          return {
            value: model.variantId,
            label: model.label,
          };
        });

        return this.replaceSelectOptions(this.ferruleSelect, ferruleOptions, selectedModel, 'No ferrules available');
      },

      replaceSelectOptions(select, options, selectedValue, emptyLabel) {
        select.replaceChildren();

        if (!options.length) {
          const emptyOption = new Option(emptyLabel, '', true, true);
          emptyOption.disabled = true;
          select.add(emptyOption);
          select.disabled = true;
          return '';
        }

        options.forEach((optionConfig) => {
          select.add(new Option(optionConfig.label, String(optionConfig.value), false, false));
        });

        select.disabled = false;
        select.value = String(selectedValue || '');

        if (select.value !== String(selectedValue || '')) {
          select.selectedIndex = 0;
        }

        return select.value;
      },

      updateShaftPreview() {
        if (!this.shaftPreviewImage || !this.shaftPreviewEmpty) {
          return;
        }

        const selectedModel = this.getSelectedShaftModel();
        const hasPreviewImage = Boolean(selectedModel?.imageSrc);

        if (hasPreviewImage) {
          this.shaftPreviewImage.src = selectedModel.imageSrc;
          this.shaftPreviewImage.alt = selectedModel.imageAlt || selectedModel.label || '';
        } else {
          this.shaftPreviewImage.removeAttribute('src');
          this.shaftPreviewImage.alt = '';
        }

        this.shaftPreviewImage.hidden = !hasPreviewImage;
        this.shaftPreviewEmpty.hidden = hasPreviewImage;
      },

      updateGripPreview() {
        if (!this.gripPreviewImage || !this.gripPreviewEmpty) {
          return;
        }

        const selectedModel = this.getSelectedGripModel();
        const hasPreviewImage = Boolean(selectedModel?.imageSrc);

        if (hasPreviewImage) {
          this.gripPreviewImage.src = selectedModel.imageSrc;
          this.gripPreviewImage.alt = selectedModel.imageAlt || selectedModel.label || '';
        } else {
          this.gripPreviewImage.removeAttribute('src');
          this.gripPreviewImage.alt = '';
        }

        this.gripPreviewImage.hidden = !hasPreviewImage;
        this.gripPreviewEmpty.hidden = hasPreviewImage;
      },

      updateFerrulePreview() {
        if (!this.ferrulePreviewImage || !this.ferrulePreviewEmpty) {
          return;
        }

        const selectedModel = this.getSelectedFerruleModel();
        const hasPreviewImage = Boolean(selectedModel?.imageSrc);

        if (hasPreviewImage) {
          this.ferrulePreviewImage.src = selectedModel.imageSrc;
          this.ferrulePreviewImage.alt = selectedModel.imageAlt || selectedModel.label || '';
        } else {
          this.ferrulePreviewImage.removeAttribute('src');
          this.ferrulePreviewImage.alt = '';
        }

        this.ferrulePreviewImage.hidden = !hasPreviewImage;
        this.ferrulePreviewEmpty.hidden = hasPreviewImage;
      },

      getSelectedSstOption() {
        if (!this.sstPuringSelect) {
          return null;
        }

        return this.sstPuringSelect.options[this.sstPuringSelect.selectedIndex] || null;
      },

      formatSummaryLabel(value) {
        return this.normalizeLabel(value).replace(/\(\+/g, '(');
      },

      getModelSummaryLabel(model) {
        if (!model) {
          return '';
        }

        return this.formatSummaryLabel(model.summaryLabel || model.label || model.title || '');
      },

      getSstPropertyLabel() {
        const selectedOption = this.getSelectedSstOption();
        return this.fields.sst_puring === "no" ? "No" : selectedOption?.dataset.miuraSummaryLabel;
      },

      getOptionInputValue(input) {
        if (!input) {
          return '';
        }

        if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) {
          return '';
        }

        return this.normalizeLabel(input.value);
      },

      getSelectedProductOptionValue(optionName) {
        if (!this.productTemplate) {
          return '';
        }

        const normalizedOptionName = this.normalizeToken(optionName);
        const optionInputs = Array.from(this.productTemplate.querySelectorAll('[name^="options["]'));

        for (const input of optionInputs) {
          const match = input.name.match(/^options\[(.+)\]$/);

          if (!match || this.normalizeToken(match[1]) !== normalizedOptionName) {
            continue;
          }

          const optionValue = this.getOptionInputValue(input);

          if (optionValue) {
            return optionValue;
          }
        }

        return '';
      },

      hasSelectedProductOption(optionName) {
        return this.getSelectedProductOptionValue(optionName) !== '';
      },

      setPropertyInputValue(propertyKey, value) {
        if (!this.propertyInputs.length) {
          return;
        }

        const normalizedValue = this.normalizeLabel(value);

        this.propertyInputs
          .filter((input) => input.dataset.miuraPropertyInput === propertyKey)
          .forEach((input) => {
            input.disabled = normalizedValue === '';
            input.value = normalizedValue;
          });
      },

      syncLinkedProductInputs(inputs, linkedVariantId) {
        if (!inputs || !inputs.length) {
          return;
        }

        const normalizedVariantId = String(linkedVariantId || '');
        const enableLinkedProduct = normalizedVariantId !== '';

        inputs.forEach((input) => {
          input.disabled = !enableLinkedProduct;
          input.value = enableLinkedProduct ? normalizedVariantId : '';
        });
      },

      syncMainProductProperties() {
        const clubNumber = this.normalizeLabel(this.fields.club_number || '');
        const selectedClubNumber = clubNumber || this.getSelectedProductOptionValue('Club Number');
        const shouldDisplayClubNumber = selectedClubNumber !== '' && !this.hasSelectedProductOption('Club Number');

        this.setPropertyInputValue('club_number', shouldDisplayClubNumber ? selectedClubNumber : '');
        this.setPropertyInputValue('length', this.fields.shaft_length);
        this.setPropertyInputValue('lie', this.fields.lie);
        this.setPropertyInputValue('loft', this.fields.loft);
        this.setPropertyInputValue('extra_wraps', this.fields.extra_wraps);
        this.setPropertyInputValue('shaft', this.getModelSummaryLabel(this.getSelectedShaftModel()));
        this.setPropertyInputValue('ferrule', this.getModelSummaryLabel(this.getSelectedFerruleModel()));
        this.setPropertyInputValue('sst', this.getSstPropertyLabel());
        this.setPropertyInputValue('grip', this.getModelSummaryLabel(this.getSelectedGripModel()));
      },

      syncShaftLinkedProduct() {
        const linkedVariantId = this.hasValidShaftSelection
          ? String(this.fields.shaft_model || '')
          : '';

        this.syncLinkedProductInputs(this.shaftLinkedInputs, linkedVariantId);
      },

      syncGripLinkedProduct() {
        const linkedVariantId = this.hasValidGripSelection
          ? String(this.fields.grip_model || '')
          : '';

        this.syncLinkedProductInputs(this.gripLinkedInputs, linkedVariantId);
      },

      syncSstPuringLinkedProduct() {
        if (!this.sstPuringSelect) {
          return;
        }

        const selectedOption = this.getSelectedSstOption();
        const linkedVariantId = String(selectedOption?.dataset.miuraLinkedVariantId || '');

        this.syncLinkedProductInputs(this.sstLinkedInputs, linkedVariantId);
      },

      syncFerruleLinkedProduct() {
        const linkedVariantId = this.hasValidFerruleSelection
          ? String(this.fields.ferrule || '')
          : '';

        this.syncLinkedProductInputs(this.ferruleLinkedInputs, linkedVariantId);
      },

      applyAddToCartState() {
        if (!this.addToCartButtons || !this.addToCartButtons.length) {
          return;
        }

        this.isSyncingAddToCart = true;

        this.addToCartButtons.forEach((button) => {
          const isNativeDisabled = button.dataset.miuraNativeDisabled === 'true';
          const shouldDisable = isNativeDisabled || !this.isValid;

          button.disabled = shouldDisable;
          button.removeAttribute('data-miura-disabled');
          button.removeAttribute('aria-disabled');
        });

        window.setTimeout(() => {
          this.isSyncingAddToCart = false;
        }, 0);
      },

      updateUI() {
        if (!this.isTabAccessible(this.activeTab)) {
          this.activeTab = this.getFirstAccessibleTab();
        }

        this.tabs.forEach((tab) => {
          const isActive = tab.dataset.miuraTab === this.activeTab;
          tab.classList.toggle('active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        this.panels.forEach((panel) => {
          const isActive = panel.dataset.miuraPanel === this.activeTab;
          panel.classList.toggle('hidden', !isActive);
          panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        Object.keys(this.getOptionButtonGroups()).forEach((fieldName) => {
          this.updateOptionButtonGroup(fieldName);
        });

        this.updateNavigationState();
      },

      updateNavigationState() {
        this.tabs.forEach((tab) => {
          const tabName = tab.dataset.miuraTab;
          const isActive = tabName === this.activeTab;
          const shouldDisable = !this.isTabAccessible(tabName) && !isActive;

          this.setNavigationDisabledState(tab, shouldDisable);
        });

        this.nextButtons.forEach((button) => {
          const panelName = button.closest('[data-miura-panel]')?.dataset.miuraPanel;
          const shouldDisable = !this.canAdvanceFromTab(panelName);

          this.setNavigationDisabledState(button, shouldDisable);
        });
      },

      setNavigationDisabledState(control, shouldDisable) {
        control.disabled = shouldDisable;
        control.removeAttribute('data-miura-disabled');
        control.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
        control.classList.toggle('opacity-50', shouldDisable);
        control.classList.toggle('cursor-not-allowed', shouldDisable);
      },

      getOptionButtonGroups() {
        return this.optionButtons.reduce((groups, button) => {
          const fieldName = button.dataset.miuraField;

          if (!groups[fieldName]) {
            groups[fieldName] = [];
          }

          groups[fieldName].push(button);
          return groups;
        }, {});
      },

      updateOptionButtonGroup(fieldName) {
        this.optionButtons
          .filter((button) => button.dataset.miuraField === fieldName)
          .forEach((button) => {
            const isSelected = this.fields[fieldName] === button.dataset.miuraValue;

            button.classList.toggle('selected', isSelected);
            button.classList.toggle('border-white', isSelected);
            button.classList.toggle('hover:border-zinc-300', isSelected);
            button.classList.toggle('border-zinc-800', !isSelected);
            button.classList.toggle('hover:border-zinc-600', !isSelected);
            button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          });
      },
    }));
  });
}
